// Tenký klient nad fetch: cookie session, hlavička X-App-Version, jednotné chyby (SPEC.md část 5).
import {
  API_PREFIX,
  APP_VERSION_HEADER,
  type ApiErrorBody,
  type ApiErrorCode,
} from '@wac-buddy/shared'
import { useUiStore } from '@/stores/ui'

export const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

export class ApiClientError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

/** Nejnižší verze z těla chyby app_outdated (details.minVersion). */
function minVersionOf(details: unknown): string | null {
  const v = (details as { minVersion?: unknown } | null | undefined)?.minVersion
  return typeof v === 'string' && v.trim() ? v.trim() : null
}

/**
 * Jedna globální cesta pro zastaralou aplikaci: 426 / app_outdated nastaví v ui store výzvu
 * k aktualizaci, takže to nemusí řešit žádný store zvlášť (App.vue ukáže OutdatedView).
 */
function flagOutdated(error: ApiClientError): void {
  if (error.code !== 'app_outdated' && error.status !== 426) return
  try {
    useUiStore().setOutdated({ minVersion: minVersionOf(error.details) })
  } catch {
    /* bez aktivní Pinia (zvláštní kontext): chyba se aspoň vyhodí dál */
  }
}

/** Převede ne-2xx odpověď na ApiClientError z JSON těla (SPEC.md část 5). */
export async function errorFromResponse(res: Response): Promise<ApiClientError> {
  let body: ApiErrorBody | null = null
  try {
    body = (await res.json()) as ApiErrorBody
  } catch {
    /* bez těla */
  }
  const error = new ApiClientError(
    body?.error.code ?? (res.status === 426 ? 'app_outdated' : 'internal'),
    body?.error.message ?? 'Něco se pokazilo. Zkus to za chvíli znovu.',
    res.status,
    body?.error.details,
  )
  flagOutdated(error)
  return error
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set(APP_VERSION_HEADER, __APP_VERSION__)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')

  const res = await fetch(`${BASE}${API_PREFIX}${path}`, {
    ...init,
    headers,
    credentials: init.credentials ?? 'include',
  })

  if (!res.ok) throw await errorFromResponse(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
